import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { Mail, ArrowLeft, CheckCircle, Eye, EyeOff, Loader2, X } from 'lucide-react';
import { authAPI } from '../services/api';
import { useThemeColors } from '../theme/ThemeContext';
import SEO from '../components/SEO';

const ResetPassword = () => {
  const colors = useThemeColors();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [tokenValid, setTokenValid] = useState(null);
  const [resetComplete, setResetComplete] = useState(false);

  const token = searchParams.get('token');

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch
  } = useForm();

  useEffect(() => {
    if (!token) {
      setTokenValid(false);
      return;
    }

    const validateToken = async () => {
      try {
        const response = await authAPI.validateResetToken(token);
        setTokenValid(response.data.success);
      } catch (error) {
        setTokenValid(false);
      }
    };

    validateToken();
  }, [token]);

  const onSubmit = async (data) => {
    if (data.newPassword !== data.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const response = await authAPI.resetPassword(token, data.newPassword);
      if (response.data.success) {
        setResetComplete(true);
        toast.success('Password reset successfully!');
      } else {
        toast.error(response.data.message || 'Failed to reset password');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (tokenValid === false) {
    return (
      <div className="min-h-screen bg-theme-background flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="text-center">
            <div className="mx-auto h-12 w-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <X className="h-6 w-6 text-red-600" />
            </div>
            <h2 className="text-3xl font-extrabold text-theme-primary">
              Invalid Reset Link
            </h2>
            <p className="mt-2 text-sm text-theme-secondary">
              This password reset link is invalid or has expired. Please request a new password reset.
            </p>
          </div>

          <div className="mt-6">
            <button
              type="button"
              onClick={() => navigate('/forgot-password')}
              className="flex items-center text-sm text-theme-secondary hover:text-theme-primary"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Request New Reset
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (resetComplete) {
    return (
      <div className="min-h-screen bg-theme-background flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="text-center">
            <CheckCircle className="mx-auto h-12 w-12 text-theme-success" />
            <h2 className="mt-4 text-lg font-medium text-theme-primary">
              Password Reset Successful
            </h2>
            <p className="mt-2 text-sm text-theme-secondary">
              Your password has been reset successfully. You can now login with your new password.
            </p>
          </div>

          <div className="mt-6">
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-theme-inverse bg-theme-primary hover:bg-theme-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-theme-primary"
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-theme-background flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <SEO 
        title="Reset Password" 
        description="Set your new TripTrot account password using the reset link sent to your email"
        keywords={['reset password', 'new password', 'account security', 'trip trot account']}
        canonical="/reset-password"
        noindex={true}
      />
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <Mail className="mx-auto h-12 w-12 text-theme-primary" />
          <h2 className="mt-6 text-3xl font-extrabold text-theme-primary">
            Reset Your Password
          </h2>
          <p className="mt-2 text-sm text-theme-secondary">
            Enter your new password below.
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div>
            <label htmlFor="newPassword" className="block text-sm font-medium text-theme-primary">
              New Password
            </label>
            <div className="mt-1 relative">
              <input
                {...register('newPassword', {
                  required: 'New password is required',
                  minLength: {
                    value: 8,
                    message: 'Password must be at least 8 characters'
                  }
                })}
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                className="appearance-none block w-full px-3 py-2 border border-theme-light rounded-md placeholder-theme-tertiary focus:outline-none focus:ring-theme-primary focus:border-theme-primary sm:text-sm pr-10"
                placeholder="Enter new password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 flex items-center pr-3"
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4 text-theme-tertiary" />
                ) : (
                  <Eye className="h-4 w-4 text-theme-tertiary" />
                )}
              </button>
            </div>
            {errors.newPassword && (
              <p className="mt-2 text-sm text-red-600">
                {errors.newPassword.message}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-theme-primary">
              Confirm New Password
            </label>
            <div className="mt-1">
              <input
                {...register('confirmPassword', {
                  required: 'Please confirm your password',
                  validate: (value) => value === watch('newPassword') || 'Passwords do not match'
                })}
                type="password"
                autoComplete="new-password"
                className="appearance-none block w-full px-3 py-2 border border-theme-light rounded-md placeholder-theme-tertiary focus:outline-none focus:ring-theme-primary focus:border-theme-primary sm:text-sm"
                placeholder="Confirm new password"
              />
            </div>
            {errors.confirmPassword && (
              <p className="mt-2 text-sm text-red-600">
                {errors.confirmPassword.message}
              </p>
            )}
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-theme-inverse bg-theme-primary hover:bg-theme-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-theme-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Resetting...
                </>
              ) : (
                'Reset Password'
              )}
            </button>
          </div>

          <div className="mt-6">
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="flex items-center text-sm text-theme-secondary hover:text-theme-primary"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to login
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;
